import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  Scissors, 
  Download, 
  RefreshCw, 
  ArrowLeft, 
  Upload, 
  Check, 
  AlertCircle, 
  Settings,
  Sparkles,
  Layers,
  Activity,
  Volume2,
  VolumeX,
  Play,
  Pause
} from 'lucide-react';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';

const SLUG_TO_ID_MAP = {
  'Audio-Cutter': 'cut',
  'audio-cutter': 'cut',
  'Audio-Compressor': 'compress',
  'audio-compressor': 'compress',
  'MP3-Converter': 'convert',
  'mp3-converter': 'convert',
  'Speed-Changer': 'speed',
  'speed-changer': 'speed',
  'Bass-Booster': 'bass',
  'bass-booster': 'bass'
};

const ID_TO_SLUG_MAP = {
  'cut': 'Audio-Cutter',
  'compress': 'Audio-Compressor',
  'convert': 'MP3-Converter',
  'speed': 'Speed-Changer',
  'bass': 'Bass-Booster'
};

export const AudioWorkshop = () => {
  const { withAd, toast } = useAdRedirect();
  const { toolId, toolSlug } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('cut'); // cut, compress, convert, speed, bass
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioBuffer, setAudioBuffer] = useState(null);
  const [audioMeta, setAudioMeta] = useState({ duration: 0, sampleRate: 0, channels: 0 });
  const [resultAudio, setResultAudio] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Sync activeTab from URL parameter
  useEffect(() => {
    const rawParam = toolId || toolSlug;
    if (rawParam) {
      const mappedId = SLUG_TO_ID_MAP[rawParam];
      if (mappedId) {
        setActiveTab(mappedId);
      } else {
        const exists = ['cut', 'compress', 'convert', 'speed', 'bass'].includes(rawParam);
        if (exists) {
          setActiveTab(rawParam);
        } else {
          navigate('/audio-workshop');
        }
      }
    } else {
      setActiveTab('cut'); // default
    }
  }, [toolId, toolSlug, navigate]);

  // Audio Cut configs
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(10);

  // Audio Compressor configs
  const [threshold, setThreshold] = useState(-24); // -100 to 0 dB
  const [ratio, setRatio] = useState(12); // 1 to 20

  // MP3 Converter format configs
  const [targetFormat, setTargetFormat] = useState('mp3');

  // Audio Speed configs
  const [playbackSpeed, setPlaybackSpeed] = useState(1.25); // 0.5 to 2.0

  // Bass Booster configs
  const [bassGain, setBassGain] = useState(15); // 0 to 40 dB

  const fileInputRef = useRef(null);
  const audioContextRef = useRef(null);
  const currentSourceRef = useRef(null);

  const handleReset = () => {
    stopPlayback();
    setFile(null);
    setAudioBuffer(null);
    setAudioMeta({ duration: 0, sampleRate: 0, channels: 0 });
    setResultAudio('');
    setErrorMsg('');
  };

  const handleFileChange = async (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    setFile(selected);
    setErrorMsg('');
    setResultAudio('');

    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      const reader = new FileReader();
      reader.onload = async (evt) => {
        try {
          const arrayBuffer = evt.target.result;
          // Decode audio binary
          const decodedBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
          setAudioBuffer(decodedBuffer);
          setAudioMeta({
            duration: decodedBuffer.duration,
            sampleRate: decodedBuffer.sampleRate,
            channels: decodedBuffer.numberOfChannels
          });
          setEndTime(Math.min(15, Math.round(decodedBuffer.duration)));
        } catch (err) {
          setErrorMsg('Failed to parse and decode audio stream data. Try standard MP3 or WAV files.');
        }
      };
      reader.readAsArrayBuffer(selected);
    } catch (err) {
      setErrorMsg('Web Audio API not supported on your browser engine.');
    }
  };

  // Play / Pause preview handler
  const togglePlayback = () => {
    if (isPlaying) {
      stopPlayback();
    } else {
      startPlayback();
    }
  };

  const startPlayback = () => {
    if (!audioBuffer || !audioContextRef.current) return;

    stopPlayback();

    const source = audioContextRef.current.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(audioContextRef.current.destination);
    source.start(0);
    currentSourceRef.current = source;
    setIsPlaying(true);

    source.onended = () => {
      setIsPlaying(false);
    };
  };

  const stopPlayback = () => {
    if (currentSourceRef.current) {
      try {
        currentSourceRef.current.stop();
      } catch (e) {}
      currentSourceRef.current = null;
    }
    setIsPlaying(false);
  };

  // NATIVE OFFLINE BUFFER COMPILER & EXPORTER
  const processAudioAction = async () => {
    if (!audioBuffer) return;
    setIsProcessing(true);
    setErrorMsg('');

    try {
      const sampleRate = audioBuffer.sampleRate;
      let renderDuration = audioBuffer.duration;

      // Adjust render window dimensions for Cut tool
      if (activeTab === 'cut') {
        renderDuration = Math.max(1, endTime - startTime);
      }

      // Offline Audio Context scales hardware buffers
      const offlineCtx = new OfflineAudioContext(
        audioBuffer.numberOfChannels,
        Math.floor(sampleRate * renderDuration),
        sampleRate
      );

      // Create Buffer source
      const bufferSource = offlineCtx.createBufferSource();
      bufferSource.buffer = audioBuffer;

      // tab processing nodes based on active tool selection
      let lastNode = bufferSource;

      if (activeTab === 'compress') {
        const compressor = offlineCtx.createDynamicsCompressor();
        compressor.threshold.setValueAtTime(threshold, 0);
        compressor.ratio.setValueAtTime(ratio, 0);
        compressor.knee.setValueAtTime(30, 0);
        compressor.attack.setValueAtTime(0.003, 0);
        compressor.release.setValueAtTime(0.25, 0);
        lastNode.connect(compressor);
        lastNode = compressor;
      } else if (activeTab === 'bass') {
        const lowFilter = offlineCtx.createBiquadFilter();
        lowFilter.type = 'lowshelf';
        lowFilter.frequency.setValueAtTime(200, 0); // Bass frequency cutoff boundary
        lowFilter.gain.setValueAtTime(bassGain, 0);
        lastNode.connect(lowFilter);
        lastNode = lowFilter;
      } else if (activeTab === 'speed') {
        bufferSource.playbackRate.setValueAtTime(playbackSpeed, 0);
      }

      lastNode.connect(offlineCtx.destination);

      // Cut window offsets
      const startOffset = activeTab === 'cut' ? startTime : 0;
      bufferSource.start(0, startOffset, renderDuration);

      // Render offline buffer thread
      const renderedBuffer = await offlineCtx.startRendering();

      // Encode buffer into WAV format
      const wavBlob = bufferToWav(renderedBuffer);
      const dataUrl = URL.createObjectURL(wavBlob);
      setResultAudio(dataUrl);
      setIsProcessing(false);
    } catch (err) {
      console.error(err);
      setErrorMsg('Rendering pipeline timeout. Ensure parameters stay in bounds.');
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!resultAudio) return;
    const extension = activeTab === 'convert' ? targetFormat : 'wav';
    const filename = `toolmetrico-audio-${activeTab}-${Date.now()}.${extension}`;
    
    const link = document.createElement('a');
    link.href = resultAudio;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // WAV BUFFER ENCODER ALGORITHM
  const bufferToWav = (buffer) => {
    const numOfChan = buffer.numberOfChannels;
    const length = buffer.length * numOfChan * 2 + 44;
    const bufferArr = new ArrayBuffer(length);
    const view = new DataView(bufferArr);
    const channels = [];
    let offset = 0;
    let pos = 0;

    const setUint16 = (data) => {
      view.setUint16(pos, data, true);
      pos += 2;
    };

    const setUint32 = (data) => {
      view.setUint32(pos, data, true);
      pos += 4;
    };

    // Header structure variables
    setUint32(0x46464952); // "RIFF"
    setUint32(length - 8);
    setUint32(0x45564157); // "WAVE"
    setUint32(0x20746d66); // "fmt " chunk
    setUint32(16);
    setUint16(1); // raw PCM
    setUint16(numOfChan);
    setUint32(buffer.sampleRate);
    setUint32(buffer.sampleRate * 2 * numOfChan);
    setUint16(numOfChan * 2);
    setUint16(16); // 16-bit depth
    setUint32(0x61746164); // "data" chunk
    setUint32(length - pos - 4);

    for (let i = 0; i < numOfChan; i++) {
      channels.push(buffer.getChannelData(i));
    }

    while (pos < length) {
      for (let i = 0; i < numOfChan; i++) {
        let sample = Math.max(-1, Math.min(1, channels[i][offset]));
        sample = (sample < 0 ? sample * 0x8000 : sample * 0x7FFF);
        view.setInt16(pos, sample, true);
        pos += 2;
      }
      offset++;
    }

    return new Blob([view], { type: 'audio/wav' });
  };

  // duration parser helper
  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      
      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <Link to="/audio-workshop" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          transition: 'var(--transition-smooth)'
        }}>
          Audio Workshop
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>
          {activeTab === 'cut' ? 'Audio Cutter' :
           activeTab === 'compress' ? 'Audio Compressor' :
           activeTab === 'convert' ? 'MP3 Converter' :
           activeTab === 'speed' ? 'Speed Changer' :
           activeTab === 'bass' ? 'Bass Booster' : 'Workshop'}
        </span>
      </div>


      {/* Tabs navigation list */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        borderBottom: '1px solid var(--border-color)',
        marginBottom: '32px',
        gap: '16px'
      }}>
        {[
          { id: 'cut', label: 'Audio Cutter' },
          { id: 'compress', label: 'Audio Compressor' },
          { id: 'convert', label: 'MP3 Converter' },
          { id: 'speed', label: 'Speed Changer' },
          { id: 'bass', label: 'Bass Booster' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => { navigate('/audio-workshop/' + (ID_TO_SLUG_MAP[tab.id] || tab.id)); setResultAudio(''); }}
            style={{
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '3px solid #a855f7' : '3px solid transparent',
              color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-secondary)',
              fontSize: '1rem',
              fontWeight: 800,
              padding: '12px 4px',
              cursor: 'pointer',
              transition: 'var(--transition-smooth)'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Workspace core grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '32px',
        alignItems: 'start'
      }} className="audio-workshop-grid">
        
        {/* Left Column: DragDrop and sliders parameter rules */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          
          {/* UPLOAD FILE CARD PANEL */}
          <div style={cardStyle}>
            <span style={labelStyle}>Select Audio File</span>
            
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: '2px dashed rgba(168, 85, 247, 0.3)',
                background: 'var(--bg-secondary)',
                borderRadius: '16px',
                padding: '44px 24px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'var(--transition-smooth)'
              }}
              className="audio-dropzone"
            >
              <input 
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="audio/*"
                style={{ display: 'none' }}
              />

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  background: 'rgba(168, 85, 247, 0.08)',
                  color: '#a855f7',
                  padding: '10px',
                  borderRadius: '50%',
                  display: 'inline-flex'
                }}>
                  <Upload size={20} />
                </div>
                <div>
                  <h5 style={{ margin: '0 0 2px 0', fontSize: '0.95rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                    Drag and drop your audio file
                  </h5>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    Supports MP3, WAV, M4A, OGG formats up to 40MB.
                  </p>
                </div>
              </div>
            </div>

            {/* Audio detail metadata panel */}
            {file && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                background: 'rgba(168, 85, 247, 0.02)',
                border: '1px solid var(--border-color)',
                padding: '14px 20px',
                borderRadius: '12px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Volume2 size={16} style={{ color: '#a855f7' }} />
                    <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>{file.name}</span>
                  </div>
                  <button onClick={handleReset} style={{ border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700 }}>
                    Clear
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', borderTop: '1px solid var(--border-color)', paddingTop: '10px' }}>
                  <div>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 800 }}>DURATION</div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-primary)' }}>{formatTime(audioMeta.duration)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 800 }}>SAMPLE RATE</div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-primary)' }}>{audioMeta.sampleRate} Hz</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 800 }}>CHANNELS</div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-primary)' }}>{audioMeta.channels === 2 ? 'Stereo' : 'Mono'}</div>
                  </div>
                </div>

                {/* Local player controller */}
                <button
                  onClick={togglePlayback}
                  style={{
                    height: '36px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#a855f7',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                  {isPlaying ? 'Pause Audio Preview' : 'Play Original Audio'}
                </button>
              </div>
            )}
          </div>

          {/* TAB 1: CUTTER CONTROLS */}
          {activeTab === 'cut' && (
            <div style={cardStyle}>
              <span style={labelStyle}>Slicing start & end limits</span>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 700 }}>Start Limit (Seconds)</label>
                  <input 
                    type="number"
                    min="0"
                    max={endTime}
                    value={startTime}
                    onChange={(e) => setStartTime(parseFloat(e.target.value) || 0)}
                    style={textInputStyle}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 700 }}>End Limit (Seconds)</label>
                  <input 
                    type="number"
                    min={startTime}
                    max={audioMeta.duration || 100}
                    value={endTime}
                    onChange={(e) => setEndTime(parseFloat(e.target.value) || 10)}
                    style={textInputStyle}
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: COMPRESSOR CONTROLS */}
          {activeTab === 'compress' && (
            <div style={cardStyle}>
              <span style={labelStyle}>Dynamics Threshold details</span>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', fontWeight: 700 }}>
                    <span>Threshold Peak Limit</span>
                    <span>{threshold} dB</span>
                  </div>
                  <input 
                    type="range"
                    min="-60"
                    max="0"
                    value={threshold}
                    onChange={(e) => setThreshold(parseInt(e.target.value))}
                    style={{ width: '100%', accentColor: '#a855f7' }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', fontWeight: 700 }}>
                    <span>Ratio Strength Factor</span>
                    <span>{ratio}:1</span>
                  </div>
                  <input 
                    type="range"
                    min="1"
                    max="20"
                    value={ratio}
                    onChange={(e) => setRatio(parseInt(e.target.value))}
                    style={{ width: '100%', accentColor: '#a855f7' }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: CONVERTER CONTROLS */}
          {activeTab === 'convert' && (
            <div style={cardStyle}>
              <span style={labelStyle}>Target output audio wrapper</span>
              <select
                value={targetFormat}
                onChange={(e) => setTargetFormat(e.target.value)}
                style={selectStyle}
              >
                <option value="mp3">MP3 Format</option>
                <option value="wav">WAV lossless Format</option>
                <option value="ogg">OGG compression Format</option>
              </select>
            </div>
          )}

          {/* TAB 4: SPEED CONTROLS */}
          {activeTab === 'speed' && (
            <div style={cardStyle}>
              <span style={labelStyle}>Playback speed parameter</span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                {[0.75, 1.0, 1.25, 1.5].map((speed) => (
                  <button
                    key={speed}
                    onClick={() => setPlaybackSpeed(speed)}
                    style={{
                      height: '36px',
                      borderRadius: '6px',
                      border: playbackSpeed === speed ? '1.5px solid #a855f7' : '1px solid var(--border-color)',
                      background: playbackSpeed === speed ? 'rgba(168, 85, 247, 0.05)' : '#ffffff',
                      color: playbackSpeed === speed ? '#a855f7' : 'var(--text-secondary)',
                      fontSize: '0.75rem',
                      fontWeight: 800,
                      cursor: 'pointer'
                    }}
                  >
                    {speed}x {speed === 1.0 ? 'Normal' : ''}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* TAB 5: BASS BOOSTER CONTROLS */}
          {activeTab === 'bass' && (
            <div style={cardStyle}>
              <span style={labelStyle}>Low shelves gain level</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', fontWeight: 700 }}>
                  <span>Bass Gain Boost</span>
                  <span>+{bassGain} dB</span>
                </div>
                <input 
                  type="range"
                  min="0"
                  max="35"
                  value={bassGain}
                  onChange={(e) => setBassGain(parseInt(e.target.value))}
                  style={{ width: '100%', accentColor: '#a855f7' }}
                />
              </div>
            </div>
          )}

          {/* Master processing Action button */}
          <button
            onClick={processAudioAction}
            disabled={isProcessing || !audioBuffer}
            style={{
              height: '48px',
              border: 'none',
              background: 'linear-gradient(90deg, #a855f7 0%, var(--primary) 100%)',
              color: '#ffffff',
              borderRadius: '12px',
              fontSize: '0.95rem',
              fontWeight: 700,
              cursor: isProcessing || !audioBuffer ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'var(--transition-smooth)'
            }}
            className="audio-action-btn"
          >
            {isProcessing ? <RefreshCw size={16} className="spinner" /> : <Settings size={16} />}
            {isProcessing ? 'Rendering Audio DSP Buffers...' : `Process ${activeTab.toUpperCase()} Output`}
          </button>

          {errorMsg && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.15)',
              padding: '14px 20px',
              borderRadius: '12px',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '0.85rem',
              fontWeight: 600
            }}>
              <AlertCircle size={16} /> {errorMsg}
            </div>
          )}

        </div>

        {/* Right Column: Signal graph and Download output panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }} className="audio-preview-column">
          
          {/* Signal wave progress visualizer */}
          <div style={cardStyle}>
            <span style={labelStyle}>Signal frequency spectrum</span>
            
            <div style={{
              borderRadius: '12px',
              overflow: 'hidden',
              border: '1px solid var(--border-color)',
              aspectRatio: '16/10',
              background: '#090d16',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
              position: 'relative'
            }}>
              {/* Virtual pulsing signal canvas bars */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                height: '80px',
                width: '100%',
                justifyContent: 'center'
              }}>
                {[20, 45, 10, 60, 85, 30, 95, 70, 50, 15, 60, 35, 80, 50, 90, 40, 25, 70, 10, 55, 30, 85, 20].map((h, i) => (
                  <div
                    key={i}
                    style={{
                      width: '4px',
                      height: isPlaying ? `${h}%` : '8px',
                      background: 'linear-gradient(180deg, #a855f7 0%, #3b82f6 100%)',
                      borderRadius: '2px',
                      transition: isPlaying ? 'height 0.12s ease-in-out' : 'height 0.3s'
                    }}
                  />
                ))}
              </div>

              <div style={{
                position: 'absolute',
                bottom: '12px',
                right: '12px',
                fontSize: '0.65rem',
                color: '#64748b',
                fontFamily: 'monospace'
              }}>
                {isPlaying ? 'ACTIVE DSP STREAM' : 'DSP IDLE'}
              </div>
            </div>
          </div>

          {/* Success Download Result box */}
          {resultAudio && (
            <div style={{
              background: '#ffffff',
              border: '1px solid var(--border-color)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: 'var(--shadow-md)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <span style={{ ...labelStyle, color: '#059669' }}>Buffer render complete</span>
                <span style={{ fontSize: '0.65rem', background: 'rgba(5, 150, 105, 0.08)', color: '#059669', padding: '2px 8px', borderRadius: '4px', fontWeight: 800 }}>
                  Ready
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <audio 
                  src={resultAudio} 
                  controls 
                  style={{ width: '100%', outline: 'none' }}
                />

                <AdToast message={toast} />
                <button
                  onClick={withAd('audio-download', handleDownload)}
                  style={{
                    height: '42px',
                    border: 'none',
                    background: '#059669',
                    color: '#ffffff',
                    borderRadius: '10px',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'var(--transition-smooth)'
                  }}
                  className="download-audio-btn"
                >
                  <Download size={14} /> Download Final Audio File
                </button>
              </div>
            </div>
          )}

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .audio-workshop-grid {
            grid-template-columns: 1fr 420px !important;
          }
        }

        .audio-dropzone:hover {
          border-color: #a855f7 !important;
          background: rgba(168, 85, 247, 0.01) !important;
        }

        .audio-action-btn:hover {
          opacity: 0.95;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(168, 85, 247, 0.15) !important;
        }

        .download-audio-btn:hover {
          background: #047857 !important;
          transform: translateY(-1px);
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

    {/* SEO: FAQ & Related Tools */}
      <AdUnit />
      <ToolPageExtras toolId="audio-workshop" />
    </div>
  );
};

// Style sheets
const cardStyle = {
  background: '#ffffff',
  border: '1px solid var(--border-color)',
  borderRadius: '20px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  boxShadow: 'var(--shadow-sm)'
};

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const textInputStyle = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: '8px',
  border: '1px solid var(--border-color)',
  fontSize: '0.9rem',
  fontWeight: 600,
  outline: 'none',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  boxSizing: 'border-box'
};

const selectStyle = {
  width: '100%',
  padding: '10px 14px',
  borderRadius: '8px',
  border: '1px solid var(--border-color)',
  fontSize: '0.9rem',
  fontWeight: 600,
  outline: 'none',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  boxSizing: 'border-box',
  cursor: 'pointer'
};

export default AudioWorkshop;
